import { toast } from 'sonner'
import { Dialog } from 'radix-ui'
import { useRoles } from '@/features/admin/roles/hooks'
import type { HaloUser } from '@/features/halocrm-user/types'
import { useAssignUserRole, useRevokeUserRole, useUserRoles } from '../hooks'
import { formatError } from './formatError'

export function UserRolesDialog({ user, onClose }: {
  user: HaloUser
  onClose: () => void
}) {
  const rolesQ = useRoles()
  const userRolesQ = useUserRoles(user.id)
  const assign = useAssignUserRole(user.id)
  const revoke = useRevokeUserRole(user.id)

  const userRoleSet = new Set<string>(userRolesQ.data ?? [])

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[520px] shadow-xl max-h-[80vh] flex flex-col">
          <Dialog.Title className="text-base font-semibold">
            Role użytkownika: {user.fullName || user.email}
          </Dialog.Title>
          <Dialog.Description className="text-xs text-slate-500 mt-1">
            Zaznaczone role są aktywne. Zmiany propagują się natychmiast (cache invalidate).
          </Dialog.Description>

          <div className="mt-4 flex-1 overflow-auto border rounded divide-y">
            {(rolesQ.isLoading || userRolesQ.isLoading) && (
              <div className="px-3 py-3 text-xs text-slate-500">Ładowanie…</div>
            )}
            {rolesQ.data?.map(r => {
              const isAssigned = userRoleSet.has(r.code)
              return (
                <label
                  key={r.code}
                  className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={isAssigned}
                    disabled={!r.isActive || assign.isPending || revoke.isPending}
                    onChange={e => {
                      if (e.target.checked) {
                        assign.mutate(r.code, { onError: err => toast.error(formatError(err)) })
                      } else {
                        revoke.mutate(r.code, { onError: err => toast.error(formatError(err)) })
                      }
                    }}
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium truncate">{r.name}</span>
                    <span className="text-[11px] text-slate-500 font-mono truncate">{r.code}</span>
                    {r.description && <span className="text-[11px] text-slate-500 truncate">{r.description}</span>}
                  </div>
                  {!r.isActive && (
                    <span className="text-[10px] text-slate-400">nieaktywna</span>
                  )}
                </label>
              )
            })}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Zamknij</button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
